import { Box, Grid, Typography, TypographyVariant, useTheme, Chip, ChipProps } from '@mui/material'
import clsx from 'clsx'
import { ReactNode } from 'react'
import { tableGridHeaderStyles, tableGridStyles } from './styles'

type HeaderProps = {
    icon?: React.ReactElement
    title: ReactNode
    titleVariant?: TypographyVariant
    info?: ReactNode
    chip?: ChipProps
    buttons?: ReactNode | ReactNode[]
    customHeader?: never
}

type CustomHeaderProps = {
    icon?: never
    title?: never
    titleVariant?: never
    info?: never
    chip?: never
    buttons?: never
    customHeader: ReactNode
}

type TableGridHeaderProps = HeaderProps | CustomHeaderProps

export const TableGridHeader = ({
    icon,
    title,
    titleVariant = 'h3',
    info,
    chip,
    buttons,
    customHeader,
}: TableGridHeaderProps): React.ReactElement => {
    const theme = useTheme()
    const classes = tableGridHeaderStyles(theme)
    const headerClass = classes.header

    if (!title) {
        return (
            <Grid
                container
                item
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                className={headerClass}
            >
                {customHeader}
            </Grid>
        )
    }
    return (
        <Grid container item direction="row" alignItems="center" justifyContent="space-between" className={headerClass}>
            <Typography variant={titleVariant} className={classes.title}>
                {title}
                {icon}
            </Typography>
            <Box>
                {info && <Box className={classes.info}>{info}</Box>}
                {chip && <Chip color={chip.color} label={chip.label} />}
                {Array.isArray(buttons) ? (
                    buttons?.map((b, i) => {
                        return (
                            <span key={i} className={classes.button}>
                                {b}
                            </span>
                        )
                    })
                ) : (
                    <span>{buttons}</span>
                )}
            </Box>
        </Grid>
    )
}

export enum SpacingVariants {
    default,
    compact,
    noPadding,
}

type TableGridProps = TableGridHeaderProps & {
    children: ReactNode
    spacingVariant?: SpacingVariants
}

/**
 * @deprecated Use C1TableGrid component instead
 */
export const TableGrid = ({
    icon,
    title,
    titleVariant = 'h4',
    info,
    chip,
    buttons,
    customHeader,
    children,
    spacingVariant = SpacingVariants.default,
}: TableGridProps): React.ReactElement => {
    const theme = useTheme()
    const classes = tableGridStyles(theme)

    const getContainerClass = (): string => {
        switch (spacingVariant) {
            case SpacingVariants.compact:
                return classes.bodyCompact
            case SpacingVariants.noPadding:
                return classes.bodyNoPadding
            case SpacingVariants.default:
            default:
                return classes.bodyDefault
        }
    }

    let header = <TableGridHeader customHeader={customHeader} />
    if (title) {
        header = (
            <TableGridHeader
                icon={icon}
                title={title}
                titleVariant={titleVariant}
                info={info}
                chip={chip}
                buttons={buttons}
            />
        )
    }

    return (
        <Grid
            container
            direction="column"
            alignItems="flex-start"
            alignContent="flex-start"
            className={classes.tableGrid}
        >
            {header}
            <Grid container item className={clsx(getContainerClass(), classes.body)}>
                {children}
            </Grid>
        </Grid>
    )
}
